/* eslint-disable react/no-unescaped-entities */
import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import React, { useContext, useEffect, useState } from 'react';
import { useTicketsStore } from 'pages/ticketV2/store/ticketsStore';
import ContainerHeader from './ContainerHeader';
import ContainerFooter from './ContainerFooter';
import ContainerBody from './ContainerBody';
import { Roles } from 'utils/constants';
import { useNetwork } from 'services/network';
import { LoaderCountContext } from 'context/LoaderV5';

import { commonDefaultValues, operatorDefaultValues } from '../index';
import { useHistory } from 'react-router';
import ContainerConsent from './ContainerConsent';
import translations from 'utils/translations';
import { useIntl } from 'react-intl';
import { format } from 'date-fns';

const AssignContainer = ({
  currentTicket,
  setCurrentTicket,
  locale,
  loggedUser,
  form,
  assignTicketTexts,
}) => {
  const intl = useIntl();
  const isOperator = loggedUser.type === Roles.OPERATOR;
  const i18nAssign = translations.TicketsV2(intl).Assign;
  const i18nErrors = i18nAssign.errors;
  const theme = useTheme();
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    stepperCurrentTicketIdx,
    submittedDiscountCodesWithEmptySpaces,
    ticketsData,
    productsInCart,
    loggedDataAtIndex,
  } = useTicketsStore();
  const history = useHistory();

  const { setLoaderActive } = useContext(LoaderCountContext);
  const network = useNetwork();

  const [isChangingTicket, setIsChangingTicket] = useState(false);

  const { getValues, setValue, watch, clearErrors, reset } = form;

  const watchedReservations = watch('reservations');
  const wPhone = watch('phone');

  const defaultValues = isOperator
    ? operatorDefaultValues
    : commonDefaultValues;

  // Effects
  useEffect(() => {
    handleAvailability(ticketsData[stepperCurrentTicketIdx]?.ticketData);
  }, [stepperCurrentTicketIdx]);

  useEffect(() => {
    if (!wPhone) {
      const timer = setTimeout(() => {
        setValue('phone', '+39');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [wPhone]);

  // Functions
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);

  const fetchAvailableDates = async (params) =>
    await network.ticketingLambda(params);

  const handleAvailability = async (ticket, resetDates) => {
    setCurrentTicket(ticket);
    showLoader();
    const startDate = format(new Date(), 'yyyy-MM-dd');
    const endDate = `${new Date().getFullYear() + 1}-12-31`;

    try {
      const res = await fetchAvailableDates({
        params: `/calendars/availability?area_code=${ticket.area.code}&pax_number=1&start_date=${startDate}&end_date=${endDate}`,
        customMethod: 'get',
        data: {},
        federationId: loggedUser?.salId,
      });

      const hadPrevAssignData = !![...useTicketsStore.getState().ticketsData][
        stepperCurrentTicketIdx
      ]?.assignData;

      const prevAssignData =
        [...useTicketsStore.getState().ticketsData][stepperCurrentTicketIdx]
          ?.assignData || form.getValues();

      const forceSelected = res.slots.length <= ticket.capacity && !isOperator;

      const nextReservations = res.slots.map((s) => ({
        date: s.date,
        groupCodes: s.groups.map((g) => g.code),
        areaCode: res.code,
        selected: forceSelected,
      }));

      const formValues =
        prevAssignData && hadPrevAssignData
          ? {
              ...prevAssignData,
              reservations:
                resetDates || !hadPrevAssignData
                  ? nextReservations.map((r) => ({
                      ...r,
                      selected: forceSelected,
                    }))
                  : prevAssignData.reservations,
              phone: !prevAssignData.phone
                ? '+39'
                : prevAssignData.phone?.includes('+')
                ? prevAssignData.phone.substring(1)
                : prevAssignData.phone,
            }
          : {
              ...defaultValues,
              phone: '+39',
              reservations: nextReservations,
            };
      if (loggedDataAtIndex === stepperCurrentTicketIdx && !prevAssignData)
        useTicketsStore.setState({ loggedDataAtIndex: null });
      reset(formValues);
    } catch (err) {
      console.error(err);
    } finally {
      hideLoader();
    }
  };

  const onToggleDate = (date) => {
    clearErrors('date');
    const reservations = getValues('reservations');
    const prevReservations = reservations.map((res) => ({
      ...res,
      ...(date === res.date &&
        res.selected && {
          selected: !res.selected,
        }),
    }));
    const selectedCount = prevReservations.filter((res) => res.selected).length;
    if (selectedCount === currentTicket.capacity && !isOperator) {
      const isSingleDay = currentTicket.capacity === 1;
      const title = `${i18nErrors.tooMuchDates.warning} ${
        currentTicket.capacity
      } ${i18nErrors.tooMuchDates[isSingleDay ? 'date' : 'dates']}`;
      return useTicketsStore.setState({
        alertData: {
          title,
          message: (
            <span>
              {i18nErrors.tooMuchDates.forThis} &nbsp;
              <a href="#dates-selection">
                {i18nErrors.tooMuchDates.onlySelect} {currentTicket.capacity}{' '}
                {i18nErrors.tooMuchDates[isSingleDay ? 'date' : 'dates']}
              </a>
            </span>
          ),
          onClose: () =>
            useTicketsStore.setState({ alertData: { severity: 'error' } }),
          closeAfter: 3000,
          severity: 'error',
        },
      });
    }
    const nextReservations = reservations.map((res) => ({
      ...res,
      ...(date === res.date &&
        (res.selected || selectedCount !== currentTicket.capacity) && {
          selected: !res.selected,
        }),
    }));
    setValue('reservations', nextReservations);
  };

  const onEdit = () => {
    setIsChangingTicket((prev) => !prev);
  };

  const onRemove = () => {
    const nextTicketsData = [...ticketsData];
    nextTicketsData.splice(stepperCurrentTicketIdx, 1);
    submittedDiscountCodesWithEmptySpaces.splice(stepperCurrentTicketIdx, 1);

    const nextProdInCart = [...productsInCart];
    const prodInCartIdx = nextProdInCart.findIndex(
      (prod) => prod.id === currentTicket.id
    );
    const targetProd = nextProdInCart[prodInCartIdx];
    nextProdInCart[prodInCartIdx] = {
      ...targetProd,
      quantity: targetProd.quantity - 1,
    };

    const isLastPositionTicket =
      stepperCurrentTicketIdx === nextTicketsData.length;

    useTicketsStore.setState({
      productsInCart: nextProdInCart,
      ticketsData: nextTicketsData,
      stepperCurrentTicketIdx:
        isLastPositionTicket && nextTicketsData.length
          ? stepperCurrentTicketIdx - 1
          : stepperCurrentTicketIdx,
    });

    if (nextTicketsData?.length)
      handleAvailability(
        nextTicketsData.length === stepperCurrentTicketIdx
          ? nextTicketsData[stepperCurrentTicketIdx - 1].ticketData
          : nextTicketsData[stepperCurrentTicketIdx].ticketData
      );
    else history.push('/');
  };

  const onChangeTicketType = async (nextTicketId) => {
    const prevProdInCartIdx = productsInCart.findIndex(
      (prod) => prod.id === currentTicket.id
    );

    const prevProdInCart = {
      ...productsInCart[prevProdInCartIdx],
      quantity: productsInCart[prevProdInCartIdx].quantity - 1,
    };

    const nextProdInCartIdx = productsInCart.findIndex(
      (prod) => prod.id === nextTicketId
    );

    const nextProdInCart = {
      ...productsInCart[nextProdInCartIdx],
      quantity: productsInCart[nextProdInCartIdx].quantity + 1,
    };

    const nextProductsInCart = [...productsInCart];
    nextProductsInCart[prevProdInCartIdx] = prevProdInCart;
    nextProductsInCart[nextProdInCartIdx] = nextProdInCart;

    const nextTicketsData = [...ticketsData];
    nextTicketsData[stepperCurrentTicketIdx] = {
      ...ticketsData[stepperCurrentTicketIdx],
      ticketData: nextProdInCart,
    };

    useTicketsStore.setState({
      productsInCart: nextProductsInCart,
      ticketsData: nextTicketsData,
    });

    setIsChangingTicket(false);
    await handleAvailability(nextProdInCart, true);
  };

  return (
    <Stack
      style={{
        width: isLgDown ? '100%' : '64%',
        ...(!isLgDown && {
          paddingRight: '70px',
        }),
      }}
      gap={'25px'}
    >
      <div>
        <Typography variant="xl" componet={'h1'}>
          {assignTicketTexts.title}
        </Typography>
        <Typography
          variant="body1"
          sx={{ mt: isSmDown ? '10px' : '25px' }}
          componet={'p'}
        >
          {assignTicketTexts.titleSubtext}
        </Typography>
      </div>
      <ContainerHeader
        currentTicket={currentTicket}
        onEdit={onEdit}
        onRemove={onRemove}
        locale={locale}
        isChangingTicket={isChangingTicket}
        loggedUser={loggedUser}
        onChangeTicketType={onChangeTicketType}
        assignTicketTexts={assignTicketTexts}
      />
      <ContainerBody
        currentTicket={currentTicket}
        isOperator={isOperator}
        locale={locale}
        form={form}
        loggedUser={loggedUser}
        assignTicketTexts={assignTicketTexts}
      />
      <ContainerFooter
        currentTicket={currentTicket}
        onToggleDate={onToggleDate}
        form={form}
        reservations={watchedReservations}
        assignTicketTexts={assignTicketTexts}
        loggedUser={loggedUser}
        locale={locale}
      />
      <ContainerConsent
        currentTicket={currentTicket}
        onToggleDate={onToggleDate}
        form={form}
        reservations={watchedReservations}
        locale={locale}
      />
    </Stack>
  );
};
export default AssignContainer;
